import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowRight, FiMail, FiStar, FiHeart, FiShield } from 'react-icons/fi';
import { GiCoral, GiTropicalFish } from 'react-icons/gi';
import { Card, Button } from '../styles/GlobalStyles';
import ReefScene from '../components/ReefScene';

const HeroSection = styled.div`
  background: linear-gradient(180deg, 
    #1565c0 0%,
    #1976d2 20%, 
    #1e88e5 40%, 
    #2196f3 60%, 
    #42a5f5 80%, 
    #64b5f6 100%);
  color: white;
  padding: 120px 0 100px;
  text-align: center;
  position: relative;
  overflow: hidden;
  min-height: 70vh;

  /* Unified ocean floor gradient - no visual separation */
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(to top, 
      rgba(13, 71, 161, 0.9) 0%, 
      rgba(25, 118, 210, 0.6) 40%, 
      rgba(33, 150, 243, 0.3) 70%,
      transparent 100%);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 10;
  background: rgba(13, 71, 161, 0.15);
  border-radius: 20px;
  padding: 3rem 2rem;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 8px 32px rgba(13, 71, 161, 0.4);
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  background: linear-gradient(45deg, #ffffff, #e3f2fd);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const BrandName = styled.span`
  font-family: 'Playfair Display', 'Cinzel', 'Cormorant Garamond', serif;
  font-weight: 600;
  font-style: italic;
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  line-height: 1.6;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 1.1rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
  }
`;

const FeaturesSection = styled.section`
  padding: 100px 0;
  background: ${props => props.theme.colors.background};
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textLight};
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FeatureCard = styled(Card)`
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-8px) scale(1.02);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
`;

const CategoriesSection = styled.section`
  padding: 100px 0;
  background: ${props => props.theme.gradients.background};
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 3rem;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
`;

const CategoryCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => !['coral'].includes(prop),
})`
  text-align: center;
  position: relative;
  overflow: hidden;
  background: ${props => props.theme.colors.white};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: ${props => props.coral ? 
      props.theme.gradients.coral : 
      props.theme.gradients.wave};
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.shadows.ocean};
  }
`;

const CategoryIcon = styled.div.withConfig({
  shouldForwardProp: (prop) => !['coral'].includes(prop),
})`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  color: ${props => props.coral ? 
    props.theme.colors.coral : 
    props.theme.colors.primaryLight};
`;

const CategoryTitle = styled.h3`
  font-size: 2rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const CategoryDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const CTASection = styled.section`
  padding: 100px 0;
  background: ${props => props.theme.gradients.wave};
  color: white;
  text-align: center;
`;

const CTAContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 0 20px;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const StatsSection = styled.section`
  padding: 80px 0;
  background: ${props => props.theme.colors.white};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 3rem;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
`;

const StatItem = styled.div`
  .stat-number {
    font-size: 3rem;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    display: block;
  }

  .stat-label {
    font-size: 1.1rem;
    color: ${props => props.theme.colors.textLight};
    font-weight: 500;
  }
`;

const Home = () => {
  return (
    <>
      <HeroSection>
        <ReefScene />
        <HeroContent>
          <HeroTitle>
            Willkommen bei<br />
            <BrandName>Riffzauber</BrandName>
          </HeroTitle>
          <HeroSubtitle>
            Meine Korallen und Fische wachsen so schnell! Private Abgabe von überschüssigen 
            Meereslebewesen aus meinem Hobby-Aquarium im Raum Zürich.
          </HeroSubtitle>
          <HeroButtons>
            <Button as={Link} to="/korallen" size="large">
              <GiCoral size={20} />
              Korallen entdecken
            </Button>
            <Button as={Link} to="/fische" size="large" secondary>
              <GiTropicalFish size={20} />
              Fische ansehen
            </Button>
          </HeroButtons>
        </HeroContent>
      </HeroSection>

      <FeaturesSection>
        <SectionTitle>Warum zu viel geworden?</SectionTitle>
        <SectionSubtitle>
          Nach einem Jahr wachsen meine Korallen und Fische schneller als erwartet - daher gebe ich gerne ab!
        </SectionSubtitle>
        <FeaturesGrid>
          <FeatureCard>
            <FeatureIcon>
              <FiHeart />
            </FeatureIcon>
            <FeatureTitle>Schnell gewachsen</FeatureTitle>
            <FeatureDescription>
              Meine Korallen und Fische gedeihen so gut in meinem Aquarium, dass sie 
              schneller wachsen als ich Platz habe. Alle Tiere sind gesund und vital!
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <FiShield />
            </FeatureIcon>
            <FeatureTitle>Zu viel für mein Becken</FeatureTitle>
            <FeatureDescription>
              Was bei mir zu viel wird, kann bei Ihnen ein neues Zuhause finden! 
              Ich gebe nur ab, was gesund ist und gut in meinem Becken funktioniert hat.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <FiStar />
            </FeatureIcon>
            <FeatureTitle>Ehrliche Beratung</FeatureTitle>
            <FeatureDescription>
              Ich bin noch relativ neu in der Meerwasseraquaristik, aber teile gerne 
              meine Erfahrungen! Ich erzähle Ihnen ehrlich, wie die Tiere bei mir waren.
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>
      </FeaturesSection>

      <CategoriesSection>
        <SectionTitle>Was wird zu viel?</SectionTitle>
        <SectionSubtitle>
          Je nach Wachstum meiner Tiere - schauen Sie regelmässig vorbei!
        </SectionSubtitle>
        <CategoriesGrid>
          <CategoryCard coral>
            <CategoryIcon coral>
              <GiCoral />
            </CategoryIcon>
            <CategoryTitle>Korallen</CategoryTitle>
            <CategoryDescription>
              Meine Korallen wachsen schneller als gedacht! Wenn welche zu gross werden 
              oder sich stark vermehren, gebe ich gerne Ableger ab.
            </CategoryDescription>
            <Button as={Link} to="/korallen" outline>
              Korallen ansehen
              <FiArrowRight size={16} />
            </Button>
          </CategoryCard>

          <CategoryCard>
            <CategoryIcon>
              <GiTropicalFish />
            </CategoryIcon>
            <CategoryTitle>Meerwasserfische</CategoryTitle>
            <CategoryDescription>
              Manchmal vermehren sich meine Fische oder werden zu gross für mein Becken. 
              Dann suche ich ein neues, liebevolles Zuhause für sie.
            </CategoryDescription>
            <Button as={Link} to="/fische" outline>
              Fische entdecken
              <FiArrowRight size={16} />
            </Button>
          </CategoryCard>
        </CategoriesGrid>
      </CategoriesSection>

      <StatsSection>
        <SectionTitle>Über mein Hobby</SectionTitle>
        <StatsGrid>
          <StatItem>
            <span className="stat-number">400L</span>
            <span className="stat-label">Riffaquarium</span>
          </StatItem>
          <StatItem>
            <span className="stat-number">Zürich</span>
            <span className="stat-label">Standort</span>
          </StatItem>
          <StatItem>
            <span className="stat-number">1+</span>
            <span className="stat-label">Jahr dabei</span>
          </StatItem>
          <StatItem>
            <span className="stat-number">❤️</span>
            <span className="stat-label">Mit Leidenschaft</span>
          </StatItem>
        </StatsGrid>
      </StatsSection>

      <CTASection>
        <CTAContent>
          <CTATitle>Haben Sie Fragen?</CTATitle>
          <CTADescription>
            Kontaktieren Sie mich gerne für eine persönliche Beratung oder bei Interesse 
            an einem meiner Meereslebewesen.
          </CTADescription>
          <Button as={Link} to="/kontakt" size="large" style={{ background: 'white', color: '#006994' }}>
            <FiMail size={20} />
            Jetzt kontaktieren
          </Button>
        </CTAContent>
      </CTASection>
    </>
  );
};

export default Home;

